import { useState } from "react";
import Card from "@/components/common/Card";
import Modal from "@/components/common/Modal";
import CreativeForm from "@/components/creatives/CreativeForm";
import { useApp } from "@/contexts/AppContext";

export default function Step3Creatives({
  form,
  segments,
  creatives,
  onCreativeCreated,
  segmentCreatives,
  onSegmentCreativesChange,
  onFieldChange,
  onNumberChange,
}) {
  const { performRequest } = useApp();
  const [newCreativeForSegment, setNewCreativeForSegment] = useState(null);

  const handleCreateCreative = async (formValues) => {
    try {
      const created = await performRequest("/creatives", {
        method: "POST",
        body: JSON.stringify(formValues),
      });
      onCreativeCreated(created);
      if (newCreativeForSegment) {
        onSegmentCreativesChange({
          ...segmentCreatives,
          [newCreativeForSegment]: created.uuid,
        });
      }
      setNewCreativeForSegment(null);
    } catch (err) {
      console.error("Failed to create creative", err);
      window.alert(
        "Failed to create creative: " + (err.message || "Unknown error"),
      );
    }
  };

  return (
    <>
      <Card className="builder-form flex flex-col gap-5">
        <div className="builder-subsection">
          <h4>Creative details</h4>
          <p className="subsection-description">
            Provide the media source and basic specifications. These values help
            validate ad delivery.
          </p>
          <div className="builder-grid four-columns">
            <div className="form-group-modal">
              <label htmlFor="campaign-creative-url">Media URL</label>
              <input
                id="campaign-creative-url"
                value={form.creativeUrl ?? ""}
                onChange={(event) =>
                  onFieldChange("creativeUrl", event.target.value)
                }
                placeholder="https://…"
                required
              />
            </div>
            <div className="form-group-modal">
              <label htmlFor="campaign-creative-duration">
                Duration (seconds)
              </label>
              <input
                id="campaign-creative-duration"
                type="number"
                min="0"
                value={form.creativeDuration ?? ""}
                onChange={(event) =>
                  onNumberChange("creativeDuration", event.target.value)
                }
                placeholder="e.g. 30"
              />
            </div>
            <div className="form-group-modal">
              <label htmlFor="campaign-creative-width">Width (px)</label>
              <input
                id="campaign-creative-width"
                type="number"
                min="0"
                value={form.creativeWidth ?? ""}
                onChange={(event) =>
                  onNumberChange("creativeWidth", event.target.value)
                }
                placeholder="e.g. 1920"
              />
            </div>
            <div className="form-group-modal">
              <label htmlFor="campaign-creative-height">Height (px)</label>
              <input
                id="campaign-creative-height"
                type="number"
                min="0"
                value={form.creativeHeight ?? ""}
                onChange={(event) =>
                  onNumberChange("creativeHeight", event.target.value)
                }
                placeholder="e.g. 1080"
              />
            </div>
          </div>
        </div>

        <div className="builder-subsection">
          <h4>Per-segment creatives</h4>
          <p className="subsection-description">
            Assign a creative to each selected audience. Leave blank to use the
            campaign-level creative.
          </p>
          {form.selectedSegments.length === 0 ? (
            <p className="form-help-text">
              No audiences selected. Pick audiences in the previous step to
              assign per-audience creatives.
            </p>
          ) : (
            form.selectedSegments.map((segUuid) => {
              const seg = segments.find((s) => s.uuid === segUuid);
              const segLabel = seg?.name ?? `Segment #${segUuid.slice(0, 6)}`;
              return (
                <div key={segUuid} className="form-group-modal">
                  <label>{segLabel}</label>
                  <div className="flex items-center gap-2">
                    <select
                      className="flex-1"
                      value={segmentCreatives[segUuid] ?? ""}
                      onChange={(e) =>
                        onSegmentCreativesChange({
                          ...segmentCreatives,
                          [segUuid]: e.target.value,
                        })
                      }
                    >
                      <option value="">— none —</option>
                      {creatives.map((c) => (
                        <option key={c.uuid} value={c.uuid}>
                          {c.name || c.url}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="btn btn-secondary whitespace-nowrap"
                      onClick={() => setNewCreativeForSegment(segUuid)}
                    >
                      + New creative
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </Card>

      {newCreativeForSegment && (
        <Modal
          isOpen={true}
          onClose={() => setNewCreativeForSegment(null)}
          title="Create new creative"
          size="medium"
        >
          <CreativeForm
            initialCreative={null}
            onSubmit={handleCreateCreative}
            onCancel={() => setNewCreativeForSegment(null)}
          />
        </Modal>
      )}
    </>
  );
}
